import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, MessageCircle, Pencil, RefreshCw, UserCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatWIB, formatApiErrorDetail } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { RegistrationForm } from "@/components/RegistrationForm";
import { REG_STATUSES, regStatusLabel, regStatusCls, ptLabel, ctLabel, waDigits, LEMBAGA_PT } from "@/lib/registrationMeta";

function Row({ label, value }) {
  return <div className="py-1.5"><p className="text-xs text-slate-400">{label}</p><p className="text-sm text-slate-800">{value || "-"}</p></div>;
}

export default function RegistrationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [convertOpen, setConvertOpen] = useState(false);
  const [convError, setConvError] = useState("");
  const [convDups, setConvDups] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/registrations/${id}`); setR(data); setNewStatus(data.status); }
    catch { toast.error("Gagal memuat pendaftaran."); navigate("/pendaftaran"); }
    finally { setLoading(false); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const saveStatus = async () => {
    setBusy(true);
    try { await api.patch(`/registrations/${id}/status`, { status_baru: newStatus, catatan: statusNote }); toast.success("Status diperbarui."); setShowStatus(false); setStatusNote(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal mengubah status."); }
    finally { setBusy(false); }
  };

  const convert = async (force = false) => {
    setBusy(true); setConvError("");
    try {
      const { data } = await api.post(`/registrations/${id}/convert?force=${force}`, {});
      toast.success(`Berhasil menjadi ${data.type === "client" ? "Klien" : "Siswa"}.`);
      setConvertOpen(false); load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.duplicates) { setConvDups(detail.duplicates); setConvError(detail.message); }
      else { setConvError(formatApiErrorDetail(detail) || e.message); }
    } finally { setBusy(false); }
  };

  if (loading || !r) return <AppLayout><div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#0f2847]" /></div></AppLayout>;
  const minor = r.participant_type === "anak" || r.participant_type === "remaja";
  const isL = LEMBAGA_PT.includes(r.participant_type);

  return (
    <AppLayout>
      <button onClick={() => navigate("/pendaftaran")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="h-4 w-4" /> Kembali ke Pendaftaran</button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-slate-900" data-testid="registration-detail-name">{r.full_name}</h1>
            <span className={`px-2 py-0.5 rounded-full text-xs border ${regStatusCls(r.status)}`} data-testid="registration-detail-status">{regStatusLabel(r.status)}</span>
            {r.converted && <span className="px-2 py-0.5 rounded-full text-xs border bg-green-50 text-green-700 border-green-200" data-testid="registration-converted-badge">Dikonversi</span>}
          </div>
          <p className="text-sm text-slate-500 font-mono mt-1">{r.reg_no}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {r.phone_normalized && <a href={`https://wa.me/${waDigits(r.phone_normalized)}`} target="_blank" rel="noreferrer" data-testid="registration-detail-wa" className="inline-flex items-center gap-1.5 text-sm bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg"><MessageCircle className="h-4 w-4" /> WhatsApp</a>}
          <button onClick={() => setShowForm(true)} data-testid="registration-detail-edit" className="inline-flex items-center gap-1.5 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-2 rounded-lg"><Pencil className="h-4 w-4" /> Edit</button>
          <button onClick={() => setShowStatus(true)} data-testid="registration-detail-change-status" className="inline-flex items-center gap-1.5 text-sm bg-[#0f2847] hover:bg-[#17416f] text-white px-3 py-2 rounded-lg"><RefreshCw className="h-4 w-4" /> Ubah Status</button>
          {!r.converted && <button onClick={() => { setConvError(""); setConvDups([]); setConvertOpen(true); }} data-testid="registration-detail-convert" className="inline-flex items-center gap-1.5 text-sm bg-[#f97316] hover:bg-[#ea580c] text-white px-3 py-2 rounded-lg"><UserCheck className="h-4 w-4" /> Jadikan {isL ? "Klien" : "Siswa"}</button>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-semibold text-slate-800 mb-2">Data Calon Siswa</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            <Row label="Jenis Peserta" value={ptLabel(r.participant_type)} />
            <Row label="Nama Panggilan" value={r.nickname} />
            {minor && <Row label="Nama Orang Tua / Wali" value={r.guardian_name} />}
            {minor && <Row label="WA Orang Tua / Wali" value={r.guardian_phone_normalized} />}
            <Row label="Nomor WhatsApp" value={r.phone_normalized} />
            <Row label="Email" value={r.email} />
            <Row label="Jenis Kelamin" value={r.gender} />
            <Row label="Tanggal Lahir" value={r.birth_date} />
            <Row label="Sekolah / Kampus / Perusahaan" value={r.institution} />
            <Row label="Kelas / Pekerjaan" value={r.class_or_job} />
            <Row label="Program yang Diminati" value={r.program_interest} />
            <Row label="Jenis Kelas" value={ctLabel(r.class_type)} />
            <Row label="Metode Belajar" value={r.mode} />
            <Row label="Waktu Belajar" value={r.desired_schedule} />
            <Row label="Tujuan Belajar" value={r.learning_goal} />
            <Row label="Sumber Informasi" value={r.source} />
          </div>
          <Row label="Catatan" value={r.notes} />
          <Row label="Persetujuan Dihubungi" value={r.consent ? "Ya" : "Tidak"} />
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-semibold text-slate-800 mb-2">Info & Riwayat</h3>
          <Row label="Tanggal Pendaftaran" value={r.registration_date} />
          <Row label="Dibuat oleh" value={r.created_by_name} />
          <Row label="Dibuat" value={formatWIB(r.created_at)} />
          <Row label="Terakhir diperbarui" value={formatWIB(r.updated_at)} />
          <div className="mt-3 border-t border-slate-100 pt-3">
            <p className="text-xs font-semibold text-slate-500 mb-2">Riwayat Perubahan Status</p>
            <ul className="space-y-2" data-testid="registration-history">
              {(r.stage_history || []).slice().reverse().map((h, i) => (
                <li key={i} className="text-xs text-slate-600 border-l-2 border-slate-200 pl-2">
                  <span className="font-medium">{h.status_lama ? `${regStatusLabel(h.status_lama)} → ` : ""}{regStatusLabel(h.status_baru)}</span>
                  <div className="text-slate-400">{formatWIB(h.waktu)} · {h.user_name || "-"}</div>
                  {h.catatan && <div className="text-slate-500">{h.catatan}</div>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {showForm && <RegistrationForm mode="edit" initial={r} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}

      {showStatus && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="registration-status-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-3">Ubah Status Pendaftaran</h3>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Status Baru</label>
            <select className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm mb-3" value={newStatus} onChange={(e) => setNewStatus(e.target.value)} data-testid="registration-status-select">
              {REG_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Catatan</label>
            <textarea className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm mb-4" rows={2} value={statusNote} onChange={(e) => setStatusNote(e.target.value)} data-testid="registration-status-note" />
            <div className="flex gap-3">
              <button onClick={() => setShowStatus(false)} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2.5 rounded-lg">Batal</button>
              <button onClick={saveStatus} disabled={busy} className="flex-1 bg-[#0f2847] hover:bg-[#17416f] text-white font-medium px-4 py-2.5 rounded-lg flex items-center justify-center gap-2" data-testid="registration-status-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Simpan</button>
            </div>
          </div>
        </div>
      )}

      {convertOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="registration-convert-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Jadikan {isL ? "Klien" : "Siswa"}</h3>
            <p className="text-sm text-slate-500 mb-4">Data pendaftaran akan digunakan untuk membuat {isL ? "Klien Lembaga" : "Siswa"}. Status pendaftaran menjadi <b>Terdaftar</b>.</p>
            {convError && (
              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                <div className="flex items-center gap-1.5 font-medium"><AlertTriangle className="h-4 w-4" /> {convError}</div>
                {convDups.length > 0 && <ul className="mt-1 list-disc pl-5 text-xs">{convDups.map((d) => <li key={d.id}>{d.full_name || d.institution_name} · {d.phone_normalized}</li>)}</ul>}
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => setConvertOpen(false)} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2.5 rounded-lg">Batal</button>
              {convDups.length > 0 && <button onClick={() => convert(true)} disabled={busy} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-medium px-4 py-2.5 rounded-lg" data-testid="registration-convert-force">Tetap Lanjut</button>}
              <button onClick={() => convert(false)} disabled={busy} className="flex-1 bg-[#f97316] hover:bg-[#ea580c] text-white font-medium px-4 py-2.5 rounded-lg flex items-center justify-center gap-2" data-testid="registration-convert-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Konversi</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
